import type { BattleInput, BattleResult } from "@zend/rules";
import { useEffect, useState } from "react";
import { playBattleOutcome } from "../audio/outcome.js";
import { itemById, type Resource } from "@zend/content";
import { pick, useT } from "../i18n/index.js";
import { navigate } from "../router.js";
import { Button } from "../ui/Button.js";
import { Counter, formatCompact } from "../ui/Counter.js";
import { GameIcon, ResourceIcon, StatIcon } from "../assets/gameIcons.js";
import { Icon } from "../ui/Icon.js";
import { battlesApi } from "../units/api.js";
import { useUnits } from "../units/state.js";
import { Stars, unitName } from "../units/shared.js";
import type { BattleReport, BattleSummary, Loot } from "../units/types.js";
import { GrowthPanel, RepairPanel, RepeatBattleButton } from "./Aftermath.js";
import { battleConclusion } from "./TacticsPanel.js";
import { ReportRevenge } from "../world/RevengeButton.js";

export function LootRow({ loot }: { loot: Loot }) {
  const t = useT();
  const entries = Object.entries(loot.resources).filter(([, v]) => v > 0);
  return (
    <div className="row" style={{ gap: 10, flexWrap: "wrap" }} data-testid="loot">
      {entries.map(([k, v]) => (
        <span key={k} className="mono loot-item">
          <ResourceIcon resource={k as Resource} size={48} label={t(`res.${k}`)} />{" "}
          <Counter value={v} compact />
        </span>
      ))}
      {loot.items.map((i) => (
        <span key={i.itemId} className="mono loot-item" title={pick(itemById(i.itemId).name)}>
          <GameIcon id={i.itemId} size={48} label={pick(itemById(i.itemId).name)} /><small>{pick(itemById(i.itemId).name)}</small><b>×{i.qty}</b>
        </span>
      ))}
      {loot.credits > 0 ? (
        <span className="mono loot-item" style={{ color: "var(--gold)" }}>
          <ResourceIcon resource="CREDITS" label={t("store.credits")} /> {loot.credits}
        </span>
      ) : null}
      {loot.xp > 0 ? (
        <span className="mono loot-item" style={{ color: "var(--accent)" }}>
          XP +{formatCompact(loot.xp)}
        </span>
      ) : null}
    </div>
  );
}

/** Per-unit summary table shared by the replay results and the quick results overlay. */
function SummaryTable({ summary, side }: { summary: BattleSummary; side: "attacker" | "defender" }) {
  const t = useT();
  return (
    <table className="summary-table">
      <thead>
        <tr>
          <th>{t(`battle.${side}`)}</th>
          <th>DMG</th>
          <th>{t("battle.taken")}</th>
          <th>{t("battle.heal")}</th>
          <th>HP</th>
        </tr>
      </thead>
      <tbody>
        {summary.units
          .filter((u) => u.side === side)
          .map((u) => (
            <tr key={u.id} className={u.id === summary.mvp ? "mvp" : ""}>
              <td>
                <span className="ellipsis" style={{ maxWidth: 120 }}>
                  {u.id === summary.mvp ? <StatIcon stat="star" size={16} label="MVP" /> : null}
                  {unitName(u.defId)}
                </span>
              </td>
              <td className="mono">{formatCompact(u.damageDealt)}</td>
              <td className="mono">{formatCompact(u.damageTaken)}</td>
              <td className="mono">{formatCompact(u.healing)}</td>
              <td className="mono">{u.alive ? `${u.hpPct}%` : <Icon name="warning" label={t("units.wrecked")} />}</td>
            </tr>
          ))}
      </tbody>
    </table>
  );
}

/** The results card at the end of a replay: stinger, loot fly-in, tables, repair all, attack again. */
export function ResultCard({
  report,
  replay,
  onClose,
}: {
  report: BattleReport;
  replay: BattleResult;
  onClose: () => void;
}) {
  const t = useT();
  const won = report.won;
  useEffect(() => {
    playBattleOutcome(won);
  }, [report.id, won]);
  return (
    <div className={`result-card glass fade-in ${won ? "won" : "lost"}`} data-testid="battle-result">
      <h2 className="stinger" style={{ color: won ? "var(--gold)" : "#fca5a5" }}>
        {won ? t("battle.victory") : t("battle.defeat")}
      </h2>
      <div className="hint">
        {report.details?.target?.kind === "nexus" ? <strong data-testid="nexus-report-wave">NEXUS · {pick({ de: `Welle ${report.details.target.wave}`, en: `Wave ${report.details.target.wave}` })} · </strong> : null}
        {t("battle.duration", { s: (replay.ticks / 10).toFixed(1) })} ·{" "}
        {replay.outcome === "timeout" ? t("battle.timeout") : ""}{" "}
        {report.opponentName ? `· ${report.opponentName}` : ""}
      </div>
      <LootRow loot={report.loot} />
      <p className="hint" data-testid="battle-reason">{battleConclusion(report.input as BattleInput, replay)}</p>
      {(report.input as BattleInput).attacker.modifiers?.revengePct ? <p className="battle-revenge" data-testid="battle-revenge-used">{t("world.revengeApplied", { n: (report.input as BattleInput).attacker.modifiers!.revengePct! })}</p> : null}
      <GrowthPanel report={report} />
      <details className="field-details battle-statistics"><summary>{pick({de:"Kampfstatistik",en:"Battle statistics"})}</summary><div className="summary-grid">
        <SummaryTable summary={report.summary} side="attacker" />
        <SummaryTable summary={report.summary} side="defender" />
      </div></details>
      <RepairPanel key={report.id} battleId={report.id} />
      <div className="row battle-result-actions" style={{ gap: 8, flexWrap: "wrap" }}>
        <ReportRevenge report={report} onPrepared={onClose} />
        <RepeatBattleButton report={report} onClose={onClose} />
        <span className="grow" />
        <Button variant="primary" onClick={onClose} data-testid="result-close">
          {t("common.close")}
        </Button>
      </div>
    </div>
  );
}

/** Quick results after a fight started from the arena or an expedition; offers the full replay. */
export function ResultsOverlay() {
  const t = useT();
  const result = useUnits((s) => s.result);
  const setResult = useUnits((s) => s.setResult);
  const [report, setReport] = useState<BattleReport | null>(null);
  const battleId = result?.outcome.battleId;
  useEffect(() => {
    let active = true;
    setReport(null);
    if (battleId)
      void battlesApi.report(battleId).then(
        (next) => {
          if (active) setReport(next);
        },
        () => {},
      );
    return () => {
      active = false;
    };
  }, [battleId]);
  useEffect(() => {
    if (result) playBattleOutcome(result.outcome.won);
  }, [result]);
  if (!result) return null;
  const { outcome, extra } = result;
  return (
    <div className="reveal" data-testid="results-overlay">
      <div
        className={`result-card glass fade-in ${outcome.won ? "won" : "lost"}`}
        style={{ position: "relative", margin: "auto" }}
      >
        <h2 className="stinger" style={{ color: outcome.won ? "var(--gold)" : "#fca5a5" }}>
          {outcome.won ? t("battle.victory") : t("battle.defeat")}
        </h2>
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <span className="hint">{t("battle.duration", { s: (outcome.summary.ticks / 10).toFixed(1) })}</span>
          {extra?.stars !== undefined ? (
            <span className="badge gold" data-testid="result-stars">
              <Stars stars={extra.stars} max={3} size={16} />
            </span>
          ) : null}
          {extra?.firstClear ? <span className="badge ok">{t("expedition.firstClear")}</span> : null}
          {extra?.ratingDelta !== undefined ? (
            <span className={`badge ${extra.ratingDelta >= 0 ? "ok" : "warn"}`} data-testid="result-rating">
              {extra.ratingDelta >= 0 ? "+" : ""}
              {extra.ratingDelta} <Icon name="arrowRight" /> {extra.rating}
            </span>
          ) : null}
          {extra?.coins !== undefined ? <span className="badge">ARC +{extra.coins}</span> : null}
        </div>
        <LootRow loot={outcome.loot} />
        {report?.id === outcome.battleId ? <p className="hint">{battleConclusion(report.input as BattleInput, report.summary)}</p> : null}
        <GrowthPanel report={report?.id === outcome.battleId ? report : null} />
        <details className="field-details battle-statistics"><summary>{pick({de:"Kampfstatistik",en:"Battle statistics"})}</summary><div className="summary-grid">
          <SummaryTable summary={outcome.summary} side="attacker" />
          <SummaryTable summary={outcome.summary} side="defender" />
        </div></details>
        <RepairPanel key={outcome.battleId} battleId={outcome.battleId} />
        <div className="row battle-result-actions" style={{ gap: 8, flexWrap: "wrap" }}>
          <ReportRevenge report={report?.id === outcome.battleId ? report : null} onPrepared={() => setResult(null)} />
          <Button
            onClick={() => {
              setResult(null);
              navigate(`/app/battle/${outcome.battleId}`);
            }}
            data-testid="result-replay"
          >
            <Icon name="play" /> {t("battle.replay")}
          </Button>
          <RepeatBattleButton
            key={outcome.battleId}
            report={report?.id === outcome.battleId ? report : null}
            onClose={() => setResult(null)}
          />
          <span className="grow" />
          <Button variant="primary" onClick={() => setResult(null)} data-testid="result-close">
            {t("common.close")}
          </Button>
        </div>
      </div>
    </div>
  );
}
